/**
 * AlertHexagonPage (shared) — draw.io-style pan/zoom canvas where every camera is a
 * hexagon, clustered by site → group. Colour encodes live state (grey=online,
 * dim=offline, red/amber/blue = critical/warning/normal alerts). Click a hex → floating
 * live preview beside it; hover an alerting hex → its alert data; a docked panel stacks
 * a live tile per active-alerting camera.
 *
 * Node-agnostic: data + real-time arrive via the injected `api` (HexAlertsApi, whose
 * adapter owns socket-vs-poll); the per-camera video player is the injected
 * PlayerComponent (local SFUPlayer / cloud LiveStreamPlayer). Cloud passes requireNode +
 * nodeKey for node-scoped lifecycle.
 *
 * All of the realtime state — counters, the live/recording debounce, tile promotion, card
 * pagination, ack/resolve — lives in `useAlertBoard`, shared with CameraMapPage. This file
 * is the hexagon LAYOUT and nothing else.
 *
 * Not routed to directly: AlertBoardPage is what the apps mount, and it renders this or
 * CameraMapPage depending on the toggle it slots into `headerSlot`.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowsPointingOutIcon,
  MinusIcon,
  PlusIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';
import HexCanvas, { type HexCanvasHandle, type Viewport } from '../components/hexagon/HexCanvas';
import HexSiteView from '../components/hexagon/HexSiteView';
import HexFilterBar, { type FilterGroup } from '../components/hexagon/HexFilterBar';
import ClickPreviewCard from '../components/hexagon/ClickPreviewCard';
import HexAlertPopover from '../components/hexagon/HexAlertPopover';
import AlertTileStack from '../components/hexagon/AlertTileStack';
import ToolbarIdentity from '../components/common/ToolbarIdentity';
import {
  HEX_W,
  layoutSites,
  type PositionedHex,
  type SiteInput,
} from '../components/hexagon/hexLayout';
import {
  ALERT_LEGEND,
  viewAlertTarget,
  type HexPlayerComponent,
  type ViewAlertRequest,
  type ViewAlertTarget,
} from '../components/hexagon/types';
import { useAlertBoard } from '../hooks/useAlertBoard';
import type { HexAlert, HexAlertsApi, HexCamera } from '../api/contracts';

const UNGROUPED = '__ungrouped__';
/** Panel id for a board with no site level — one background for the whole honeycomb. */
const BOARD_PANEL = '__board__';
/** The "no restriction" chip key, for every single-choice filter group. */
const ALL = '__all__';
/**
 * Where the board starts, and where "Fit" returns it to. A measured width-fit lands near
 * 100%, which shows fewer rows than operators want at a glance; 69% is the level they
 * settled on, so it is the one both the initial render and Fit are pinned to.
 */
const DEFAULT_ZOOM = 0.69;

type StateFilter = 'all' | 'alerting' | 'offline';

export interface AlertHexagonPageProps {
  api: HexAlertsApi;
  PlayerComponent: HexPlayerComponent;
  /** Cloud: require a selected node; show an empty state when nodeKey is falsy. */
  requireNode?: boolean;
  /** Cloud: changes when the selected node changes → reload cameras + re-watch. */
  nodeKey?: string;
  className?: string;
  /** Optional: when set, alert tiles show a "View alert" link that calls this (each app
   *  routes to its own alert lifecycle page). */
  onViewAlert?: (alert: ViewAlertRequest) => void;
  /** Slotted into the right of the toolbar. AlertBoardPage puts the map/honeycomb toggle
   *  here so it sits in the same place on both boards. */
  headerSlot?: ReactNode;
  /** Jump to a camera's recorded footage from the live preview. Omit when the caller lacks
   *  `recordings.view` — that is what hides the button. */
  onOpenRecordings?: (cameraId: string) => void;
  /**
   * This board covers ONE site — draw every group on a single shared background, with no
   * per-site panels and no site filter.
   *
   * The local node is one place by definition, but its camera groups are a tree and the
   * board derives a "site" from each group's ROOT. Where an installation keeps its groups
   * flat, that made every group its own site: a panel per group, each captioned with the
   * group's own name twice over. Local passes this; cloud leaves it off, where sites are
   * genuinely different places. A board whose cameras show no site level above their groups
   * is treated this way regardless — there is nothing to frame.
   */
  singleSite?: boolean;
}

export default function AlertHexagonPage({
  api,
  PlayerComponent,
  requireNode = false,
  nodeKey,
  className = 'p-3 sm:p-4',
  onViewAlert,
  headerSlot,
  onOpenRecordings,
  singleSite = false,
}: AlertHexagonPageProps) {
  const blocked = requireNode && !nodeKey;

  // The hexagon shows EVERY camera, so no camera filter.
  const board = useAlertBoard({ api, blocked, nodeKey });
  const {
    cameras,
    loading,
    counters,
    recent,
    cards,
    liveTiles,
    primaryCameraId,
    heldCameraIds,
    warmupCameraId,
    nameById,
    liveFor,
    recordingFor,
    colorStateFor,
    counterFor,
    latestAlertFor,
  } = board;

  const canvasRef = useRef<HexCanvasHandle>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [vp, setVp] = useState<Viewport>({ tx: 0, ty: 0, scale: 1 });
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const onViewportChange = useCallback((v: Viewport) => setVp(v), []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  // ── Filters ───────────────────────────────────────────────────────────────
  // SITE narrows what is LAID OUT (a site you aren't watching shouldn't cost canvas);
  // STATE and ALERT only dim, so the honeycomb an operator has learned never reflows
  // under them. See CameraHex's `dimmed`.
  const [siteFilter, setSiteFilter] = useState<string>(ALL);
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [alertFilter, setAlertFilter] = useState<string>(ALL);

  /** A camera's site panel name. Cameras in no group belong to no site — they are collected
   *  under one honest "Ungrouped" panel rather than invented into a site. */
  const siteNameOf = useCallback(
    (cam: HexCamera) =>
      (cam.siteId || UNGROUPED) === UNGROUPED ? 'Ungrouped' : cam.siteName || 'Site',
    [],
  );

  /** Do these cameras actually sit under a site ABOVE their group, or is "site" just the
   *  group looked at from one level up? Only the former is worth a panel each. */
  const hasSiteLevel = useMemo(
    () => cameras.some((c) => c.siteId && c.siteId !== UNGROUPED && c.siteId !== c.groupId),
    [cameras],
  );
  const mergeSites = singleSite || !hasSiteLevel;

  const siteOptions = useMemo(() => {
    const m = new Map<string, { id: string; name: string; count: number }>();
    cameras.forEach((cam) => {
      const id = cam.siteId || UNGROUPED;
      const hit = m.get(id);
      if (hit) hit.count += 1;
      else m.set(id, { id, name: siteNameOf(cam), count: 1 });
    });
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [cameras, siteNameOf]);

  // A site that disappears (node switch, camera moved) must not leave the board empty with
  // no visible reason — fall back to everything.
  useEffect(() => {
    if (siteFilter !== ALL && !siteOptions.some((s) => s.id === siteFilter)) setSiteFilter(ALL);
  }, [siteOptions, siteFilter]);

  // With one site there is nothing to filter BY, so any remembered choice is inert.
  const activeSiteFilter = mergeSites ? ALL : siteFilter;

  const scopedCameras = useMemo(
    () =>
      activeSiteFilter === ALL
        ? cameras
        : cameras.filter((c) => (c.siteId || UNGROUPED) === activeSiteFilter),
    [cameras, activeSiteFilter],
  );

  /** cameraId → the names of the alerts currently active on it. Both feeds hold only
   *  active alerts, and between them they cover the streaming arrivals and the paged list. */
  const alertNamesByCamera = useMemo(() => {
    const m = new Map<string, Set<string>>();
    const add = (a: HexAlert) => {
      const name = (a.alertName || a.eventType || '').trim();
      if (!a.cameraId || !name) return;
      const hit = m.get(a.cameraId);
      if (hit) hit.add(name);
      else m.set(a.cameraId, new Set([name]));
    };
    recent.forEach(add);
    cards.forEach(add);
    return m;
  }, [recent, cards]);

  /** Each active alert name and how many cameras in scope are carrying it. */
  const alertOptions = useMemo(() => {
    const counts = new Map<string, number>();
    scopedCameras.forEach((cam) => {
      alertNamesByCamera.get(cam.id)?.forEach((n) => counts.set(n, (counts.get(n) || 0) + 1));
    });
    return [...counts.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [scopedCameras, alertNamesByCamera]);

  // Alerts clear as operators work them. A filter left pointing at a name that no longer
  // fires would silently grey the whole board out, so it releases itself.
  useEffect(() => {
    if (alertFilter !== ALL && !alertOptions.some((o) => o.name === alertFilter)) setAlertFilter(ALL);
  }, [alertOptions, alertFilter]);

  const isAlerting = useCallback((cam: HexCamera) => (counterFor(cam)?.total || 0) > 0, [counterFor]);

  const stateCounts = useMemo(() => {
    let alerting = 0;
    let offline = 0;
    scopedCameras.forEach((cam) => {
      if (isAlerting(cam)) alerting += 1;
      else if (colorStateFor(cam) === 'offline') offline += 1;
    });
    return { all: scopedCameras.length, alerting, offline };
  }, [scopedCameras, isAlerting, colorStateFor]);

  const isDimmed = useCallback(
    (cam: HexCamera): boolean => {
      if (stateFilter === 'alerting' && !isAlerting(cam)) return true;
      if (stateFilter === 'offline' && colorStateFor(cam) !== 'offline') return true;
      if (alertFilter !== ALL && !alertNamesByCamera.get(cam.id)?.has(alertFilter)) return true;
      return false;
    },
    [stateFilter, alertFilter, isAlerting, colorStateFor, alertNamesByCamera],
  );

  /** How many cameras survive the filters — the number the row reports back. */
  const matchCount = useMemo(
    () => scopedCameras.reduce((n, cam) => (isDimmed(cam) ? n : n + 1), 0),
    [scopedCameras, isDimmed],
  );

  const filterGroups = useMemo<FilterGroup[]>(() => {
    const groups: FilterGroup[] = [];
    // One site is the local node's whole world — a chip row offering "All sites" and the
    // only site says nothing. The row grows this group back when cloud gives it real sites.
    if (!mergeSites) {
      groups.push({
        label: 'Site',
        value: siteFilter,
        // Picking a site REBUILDS the board around it — the other sites' cameras are not
        // dimmed, they're not drawn. The result line says so.
        effect: 'hide',
        onChange: setSiteFilter,
        chips: [
          { key: ALL, label: 'All sites', count: cameras.length },
          ...siteOptions.map((s) => ({ key: s.id, label: s.name, count: s.count })),
        ],
      });
    }
    // "Camera state", not "State": on a page that also filters by ALERT state, a bare
    // "State" leaves the reader working out which of the two things it means.
    groups.push({
      label: 'Camera state',
      value: stateFilter,
      onChange: (k) => setStateFilter(k as StateFilter),
      chips: [
        // "All cameras", not "Any": every other filter on this board and in the alert list
        // opens with All — All sites, All alerts, All severities — so one row reading "Any"
        // made the reset chip look like a different kind of option than its neighbours.
        { key: 'all', label: 'All cameras', count: stateCounts.all, dot: 'bg-slate-400' },
        { key: 'alerting', label: 'Alerting', count: stateCounts.alerting, dot: 'bg-red-500' },
        { key: 'offline', label: 'Offline', count: stateCounts.offline, dot: 'bg-slate-600' },
      ],
    });
    groups.push({
      label: 'Alert type',
      value: alertFilter,
      onChange: setAlertFilter,
      chips: [
        { key: ALL, label: 'All alerts', count: stateCounts.alerting },
        ...alertOptions.map((o) => ({
          key: o.name,
          label: o.name,
          count: o.count,
          title: `Show only cameras with a "${o.name}" alert — ${o.count} camera${o.count === 1 ? '' : 's'}`,
        })),
      ],
    });
    return groups;
  }, [mergeSites, cameras.length, siteOptions, siteFilter, stateFilter, stateCounts, alertOptions, alertFilter]);

  // ── Layout ────────────────────────────────────────────────────────────────
  /** Bucket cameras into group columns; ungrouped cameras sort last. */
  const clustersOf = useCallback((cams: HexCamera[], panelId: string) => {
    const groups = new Map<string, HexCamera[]>();
    cams.forEach((cam) => {
      const gid = cam.groupId ?? UNGROUPED;
      const arr = groups.get(gid);
      if (arr) arr.push(cam);
      else groups.set(gid, [cam]);
    });
    return [...groups.entries()]
      .map(([gid, items]) => ({
        id: `${panelId}::${gid}`,
        ungrouped: gid === UNGROUPED,
        title: gid === UNGROUPED ? 'Ungrouped' : items[0].groupName || 'Group',
        items: [...items].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) =>
        a.ungrouped !== b.ungrouped ? (a.ungrouped ? 1 : -1) : a.title.localeCompare(b.title),
      )
      .map(({ ungrouped: _ungrouped, ...cluster }) => cluster);
  }, []);

  const siteInputs: SiteInput<HexCamera>[] = useMemo(() => {
    // ONE site: every group shares a single background panel, with no site header above it.
    // Splitting a one-site board into a panel per top-level group made each group look like
    // a separate place and repeated its name twice — once as the panel, once as the group.
    if (mergeSites) {
      // Deliberately NAMELESS, even where the caller knows the site's name: on a one-site
      // board every hex on screen belongs to it, so printing it above them labels nothing —
      // it just spends a line of the canvas restating the page the operator is already on.
      // An empty name is also what tells layoutSites to reserve no header band at all.
      return [{ id: BOARD_PANEL, name: '', clusters: clustersOf(scopedCameras, BOARD_PANEL) }];
    }

    const sites = new Map<string, { id: string; name: string; cams: HexCamera[] }>();
    scopedCameras.forEach((cam) => {
      const sid = cam.siteId || UNGROUPED;
      const hit = sites.get(sid);
      if (hit) hit.cams.push(cam);
      else sites.set(sid, { id: sid, name: siteNameOf(cam), cams: [cam] });
    });

    return [...sites.values()]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((site) => ({ id: site.id, name: site.name, clusters: clustersOf(site.cams, site.id) }));
  }, [mergeSites, scopedCameras, siteNameOf, clustersOf]);

  /**
   * The width the honeycomb flows into: the canvas element's own width, quantised so a
   * drag-resize doesn't re-run the layout on every pixel. `size.w` is 0 for the first frame
   * (before the ResizeObserver fires), which layoutSites reads as "not measured yet".
   */
  // Rounded DOWN, so the laid-out board never ends up fractionally wider than the canvas it
  // was measured against (which would scale the whole thing below 1:1 to fit).
  const flowWidth = useMemo(() => (size.w > 0 ? Math.floor(size.w / 16) * 16 : 0), [size.w]);

  const layout = useMemo(
    () => layoutSites(siteInputs, { targetWidth: flowWidth }),
    [siteInputs, flowWidth],
  );

  const hexById = useMemo(() => {
    const m: Record<string, PositionedHex<HexCamera>> = {};
    layout.hexes.forEach((h) => (m[h.item.id] = h));
    return m;
  }, [layout]);

  const selectedHex = selectedId ? hexById[selectedId] : undefined;
  const hoveredHex = hoveredId ? hexById[hoveredId] : undefined;
  const hoveredAlert = hoveredId ? latestAlertFor(hoveredId) : undefined;

  const screenOf = (cx: number, cy: number) => ({ x: vp.tx + cx * vp.scale, y: vp.ty + cy * vp.scale });
  const hexHalfW = (HEX_W / 2) * vp.scale;

  const handleSelect = useCallback((hex: PositionedHex<HexCamera>) => {
    setSelectedId((cur) => (cur === hex.item.id ? null : hex.item.id));
  }, []);

  /**
   * Every alert the panel is showing, in the order it shows them, reduced to what a detail view
   * needs. The click copies this, so paging with the arrow keys stays stable while the record is
   * open even as the live feed moves on.
   */
  const alertSiblings = useMemo<ViewAlertTarget[]>(
    () => cards.map((c) => viewAlertTarget(c, nameById)),
    [cards, nameById],
  );

  const handleViewAlert = useCallback(
    (alert: HexAlert) =>
      onViewAlert?.({
        ...viewAlertTarget(alert, nameById),
        siblings: alertSiblings,
        // A live tile's alert is also in `cards` (those hold every active alert), but if it
        // somehow isn't, -1 → 0 makes this a list of one rather than a broken cursor.
        index: Math.max(0, alertSiblings.findIndex((s) => !!alert.id && s.id === alert.id)),
      }),
    [onViewAlert, nameById, alertSiblings],
  );

  /** Active alerts across every camera on the board — the centre zone's headline number.
   *  Summed from the counters rather than `cards`, which is a paginated slice. */
  const activeAlerts = useMemo(
    () => Object.values(counters).reduce((n, c) => n + (c?.total || 0), 0),
    [counters],
  );

  const selCaption = selectedHex
    ? latestAlertFor(selectedHex.item.id)?.alertName ||
      latestAlertFor(selectedHex.item.id)?.eventType ||
      'live'
    : '';

  return (
    <div className={`flex h-full flex-col gap-3 ${className}`}>
      {/* Three zones, matching CameraMapPage exactly so the bar cannot reflow when the operator
          flips the view: left = what am I looking at, centre = the view toggle, right = actions.
          The page's own name is gone from here — the sidebar and the title bar already say
          which page this is, and it was crowding the one thing the bar has to report. The map
          board names its layout on the left; the hexagon has no layout, so its equivalent is
          the size of the board and how much of it is alerting.
          Both side zones are `flex-1` so the toggle sits optically CENTRED even though the
          hexagon's right zone is empty and the map's is not. */}
      <div className="flex flex-shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 sm:px-3 sm:py-2">
        {/* ─── Left zone — which board, then how big it is and what's lit up ───
             The headline names the VIEW, not the camera count: an operator landing here (or
             flipping the toggle) needs to know which of the two boards they're on before the
             statistics mean anything, and a bare "12 cameras" answered a question they hadn't
             asked yet. "Honeycomb" alone is a shape, not a meaning, so the words that follow it
             say what it shows and the second line names the page it belongs to. The
             active-alert total keeps its red there, so a live board still shouts. */}
        <div className="flex min-w-0 flex-1 items-center">
          <ToolbarIdentity
            icon={RectangleGroupIcon}
            // A ONE-SITE board doesn't name its site here: local has only one, and cloud's
            // switcher sits directly above and names the node it is showing. Repeating that
            // spent the toolbar's most prominent line saying what the operator had just
            // chosen, so the corner names the VIEW and nothing else.
            title="Honeycomb view"
            titleText="Honeycomb view — every camera, clustered by group"
            detail={
              blocked ? 'Alert board · no site selected' : loading ? 'Alert board · loading cameras…' : (
                <>
                  {'Alert board · '}
                  {/* On a cross-site board the camera count alone is not the size of what
                      you're looking at — how many PLACES those cameras are in is the first
                      thing an operator scanning the estate needs. */}
                  {!mergeSites && siteOptions.length > 0 && (
                    <>
                      {siteOptions.length} site{siteOptions.length === 1 ? '' : 's'}
                      {' · '}
                    </>
                  )}
                  {cameras.length} camera{cameras.length === 1 ? '' : 's'}
                  {' · '}
                  <span className={activeAlerts > 0 ? 'font-semibold text-red-500' : ''}>
                    {activeAlerts > 0
                      ? `${activeAlerts} active alert${activeAlerts === 1 ? '' : 's'}`
                      : 'no active alerts'}
                  </span>
                </>
              )
            }
          />
        </div>

        {/* ─── Centre zone — which view you're on ─── */}
        <div className="flex flex-none items-center justify-center">{headerSlot}</div>

        {/* ─── Right zone — actions. Empty on this board (the honeycomb has no layout to
             manage); the zone stays so the toggle keeps its place across the toggle. ─── */}
        <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:gap-2" />
      </div>

      {/* ─── Filter row — its own line, under the identity bar ───
           Not squeezed into the toolbar: the chips carry live counts that change under the
           operator, and a bar that reflows every time a camera alerts would move the view
           toggle out from under their cursor. */}
      {!blocked && !loading && cameras.length > 0 && (
        <HexFilterBar
          groups={filterGroups}
          /* What the choices did, in cameras. The board can't say this itself: a dimmed
             hexagon looks exactly like a camera with nothing happening on it. */
          matchCount={matchCount}
          totalCount={cameras.length}
        />
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row lg:gap-4">
        <div ref={wrapperRef} className="relative h-[45vh] min-h-[260px] flex-none overflow-hidden rounded-lg border border-border bg-card lg:h-auto lg:min-h-0 lg:flex-1">
          {blocked ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Select a site to view its cameras.
            </div>
          ) : loading ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading cameras…</div>
          ) : layout.sites.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">No cameras to display.</div>
          ) : (
            <HexCanvas
              ref={canvasRef}
              contentWidth={layout.width}
              contentHeight={layout.height}
              fitMode="width"
              fitScale={DEFAULT_ZOOM}
              onViewportChange={onViewportChange}
            >
              {layout.sites.map((site) => (
                <HexSiteView
                  key={site.id}
                  site={site}
                  selectedId={selectedId}
                  colorStateFor={colorStateFor}
                  counterFor={counterFor}
                  liveFor={liveFor}
                  recordingFor={recordingFor}
                  isDimmed={isDimmed}
                  onSelect={handleSelect}
                  onHoverStart={(hex) => setHoveredId(hex.item.id)}
                  onHoverEnd={() => setHoveredId(null)}
                />
              ))}
            </HexCanvas>
          )}

          {/* Screen-space overlay layer (not scaled by the canvas transform) */}
          <div className="pointer-events-none absolute inset-0 z-20">
            {selectedHex &&
              (() => {
                const p = screenOf(selectedHex.cx, selectedHex.cy);
                return (
                  <ClickPreviewCard
                    cameraId={selectedHex.item.id}
                    name={selectedHex.item.name}
                    caption={selCaption}
                    live={liveFor(selectedHex.item)}
                    recording={recordingFor(selectedHex.item)}
                    counter={counterFor(selectedHex.item)}
                    anchorX={p.x}
                    anchorY={p.y}
                    hexHalfW={hexHalfW}
                    viewportW={size.w}
                    viewportH={size.h}
                    PlayerComponent={PlayerComponent}
                    onOpenRecordings={onOpenRecordings}
                    onClose={() => setSelectedId(null)}
                  />
                );
              })()}
            {hoveredHex && hoveredId !== selectedId &&
              (() => {
                const p = screenOf(hoveredHex.cx, hoveredHex.cy);
                return (
                  <HexAlertPopover
                    alert={hoveredAlert}
                    name={hoveredHex.item.name}
                    live={liveFor(hoveredHex.item)}
                    recording={recordingFor(hoveredHex.item)}
                    counter={counterFor(hoveredHex.item)}
                    anchorX={p.x}
                    anchorY={p.y}
                    hexHalfW={hexHalfW}
                    viewportW={size.w}
                    viewportH={size.h}
                  />
                );
              })()}
          </div>

          {/* Zoom cluster. "Fit" alone was the only way to change scale, which made every
              other zoom a mouse-wheel guess; the read-out is what turns that into a control
              an operator can return to a known level with. */}
          {!blocked && layout.sites.length > 0 && (
            <div className="absolute right-3 top-3 z-40 flex items-center gap-0.5 rounded-lg border border-border bg-card/90 p-1 shadow-card backdrop-blur-sm">
              <button
                type="button"
                title="Zoom out"
                aria-label="Zoom out"
                onClick={() => canvasRef.current?.zoomBy(1 / 1.2)}
                className="rounded-md p-1 text-foreground hover:bg-muted"
              >
                <MinusIcon className="h-3.5 w-3.5" />
              </button>
              <span className="w-11 text-center text-xs font-semibold tabular-nums text-foreground">
                {Math.round(vp.scale * 100)}%
              </span>
              <button
                type="button"
                title="Zoom in"
                aria-label="Zoom in"
                onClick={() => canvasRef.current?.zoomBy(1.2)}
                className="rounded-md p-1 text-foreground hover:bg-muted"
              >
                <PlusIcon className="h-3.5 w-3.5" />
              </button>
              <span className="mx-1 h-4 w-px bg-border" aria-hidden />
              <button
                type="button"
                onClick={() => canvasRef.current?.fitToView()}
                className="flex items-center gap-1 rounded-md px-1.5 py-1 text-xs text-foreground hover:bg-muted"
              >
                <ArrowsPointingOutIcon className="h-3.5 w-3.5" /> Fit
              </button>
            </div>
          )}

          <div className="absolute bottom-3 left-3 z-40 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border bg-card/90 px-3 py-1.5 shadow-card backdrop-blur-sm">
            <span className="text-[10px] font-semibold text-foreground">
              Each hexagon — left dot: Live, right dot: Recording
            </span>
            <span className="h-3 w-px bg-border" aria-hidden />
            {ALERT_LEGEND.map((l) => (
              <span key={l.label} className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground">
                <span className={`inline-block h-2.5 w-2.5 rounded-full ${l.c} ${l.pulse ? 'animate-pulse' : ''}`} />
                {l.label}
              </span>
            ))}
          </div>
        </div>

        {/* Hidden transport warmer — keeps the WebRTC ICE/DTLS transport negotiated
            while no live tiles exist, so the next alert renders without a cold handshake.
            Rendered off-screen with no overlay; it only holds a subscription. */}
        {warmupCameraId && (
          <div aria-hidden className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0">
            <PlayerComponent cameraId={warmupCameraId} label="warmup" className="h-px w-px" />
          </div>
        )}

        <aside className="flex min-h-0 flex-1 flex-col rounded-lg border border-border bg-card p-3 lg:w-[360px] lg:flex-none lg:p-4">
          <AlertTileStack
            liveTiles={liveTiles}
            persistentCameraId={primaryCameraId}
            heldCameraIds={heldCameraIds}
            cards={cards}
            nameById={nameById}
            counters={board.counters}
            PlayerComponent={PlayerComponent}
            onSelect={(id) => setSelectedId(id)}
            onExpire={board.handleTileExpire}
            onContinue={board.handleTileContinue}
            onAcknowledge={api.acknowledgeAlert ? board.handleAcknowledge : undefined}
            onResolve={api.resolveAlert ? board.handleResolve : undefined}
            onViewAlert={onViewAlert ? handleViewAlert : undefined}
            onOpenRecordings={onOpenRecordings}
            loadPreview={api.loadPreview}
            hasMore={board.hasMore}
            loadingMore={board.loadingCards}
            onLoadMore={board.loadMoreCards}
          />
        </aside>
      </div>
    </div>
  );
}
